import { create } from "zustand";

// UI-only chat state. Persistent data (messages, conversation membership)
// lives in Apollo's normalized cache; this store holds the bits Apollo
// can't: the bottom dock state (which conversations have a floating window
// open, which are minimized, whether the message list is expanded) plus the
// per-conversation transient typing + unread bookkeeping the subscription
// pushes between query refetches.

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  sentAt: string;
}

export interface ConversationMeta {
  id: string;
  participantIds: string[];
  lastMessage?: ChatMessage;
  unreadCount: number;
  unreadUpdatedAt?: string;
}

// Presence + read receipts carry their server timestamp so a late-
// resolving seed query can't roll back a newer live event (and vice
// versa). We only ever accept strictly newer updates per person.
export interface PresenceState {
  online: boolean;
  observedAt: string;
}

export interface ReadReceiptState {
  lastReadMessageId: string;
  readAt: string;
}

// True when `incoming` is strictly newer than `current`. Tolerates
// missing/unparseable timestamps by accepting the update (better to
// apply than to silently drop a real change).
function isNewer(incoming: string, current: string): boolean {
  const a = Date.parse(incoming);
  const b = Date.parse(current);
  if (Number.isNaN(a) || Number.isNaN(b)) return true;
  return a > b;
}

// Fallback until the dock measures the viewport (see ChatDock).
const DEFAULT_CAPACITY = 3;

// Minimize the oldest visible windows until at most `capacity` remain open
// side by side. `openChats` is ordered oldest→newest, so we walk from the
// front. `keepId` (the window just opened/restored) is never minimized.
function enforceCapacity(
  openChats: string[],
  minimized: Set<string>,
  capacity: number,
  keepId?: string,
): Set<string> {
  const next = new Set(minimized);
  const visible = openChats.filter((id) => !next.has(id));
  let over = visible.length - Math.max(1, capacity);
  if (over <= 0) return minimized;
  for (const id of visible) {
    if (over <= 0) break;
    if (id === keepId) continue;
    next.add(id);
    over--;
  }
  return next;
}

function mergeMessages(existing: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const msg of existing) byId.set(msg.id, msg);
  for (const msg of incoming) byId.set(msg.id, msg);
  return [...byId.values()].sort((a, b) => {
    const byTime = a.sentAt.localeCompare(b.sentAt);
    return byTime !== 0 ? byTime : a.id.localeCompare(b.id);
  });
}

interface ChatState {
  myId: string | null;
  conversations: Map<string, ConversationMeta>;
  messages: Map<string, ChatMessage[]>;
  typing: Map<string, Set<string>>;
  // conversationId -> (personId -> presence). Other participants' live
  // presence, seeded from ConversationPresence and kept fresh by
  // PRESENCE_CHANGED events (both wired in ChatDock's bridge).
  presence: Map<string, Map<string, PresenceState>>;
  // conversationId -> (personId -> read receipt). Drives the "Seen"
  // marker; seeded from ConversationReadReceipts, updated by
  // READ_RECEIPT_UPDATED events.
  readReceipts: Map<string, Map<string, ReadReceiptState>>;
  // Conversation IDs whose live subscription is currently errored.
  // Written by the single live bridge (ChatDock), read by the open
  // window (ChatView) to show the "live updates unavailable" banner.
  liveErrors: Set<string>;

  // Bottom dock state.
  listOpen: boolean;
  openChats: string[];
  minimized: Set<string>;
  capacity: number;

  toggleList: () => void;
  setListOpen: (open: boolean) => void;
  setCapacity: (capacity: number) => void;
  openChat: (id: string) => void;
  closeChat: (id: string) => void;
  minimizeChat: (id: string) => void;

  setMyId: (id: string | null) => void;
  setConversations: (convos: ConversationMeta[]) => void;
  addConversation: (convo: ConversationMeta) => void;
  removeConversation: (id: string) => void;
  setMessages: (conversationId: string, msgs: ChatMessage[]) => void;
  addMessage: (msg: ChatMessage) => void;
  setLastMessage: (conversationId: string, msg: ChatMessage) => void;
  setUnreadCount: (conversationId: string, count: number) => void;
  setTyping: (conversationId: string, personId: string, isTyping: boolean) => void;
  setPresence: (
    conversationId: string,
    personId: string,
    online: boolean,
    observedAt: string,
  ) => void;
  setReadReceipt: (
    conversationId: string,
    personId: string,
    lastReadMessageId: string,
    readAt: string,
  ) => void;
  setLiveError: (conversationId: string, hasError: boolean) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  myId: null,
  conversations: new Map(),
  messages: new Map(),
  typing: new Map(),
  presence: new Map(),
  readReceipts: new Map(),
  liveErrors: new Set(),

  listOpen: false,
  openChats: [],
  minimized: new Set(),
  capacity: DEFAULT_CAPACITY,

  toggleList: () => set((s) => ({ listOpen: !s.listOpen })),
  setListOpen: (listOpen) => set({ listOpen }),

  setCapacity: (capacity) =>
    set((s) => {
      if (capacity === s.capacity) return s;
      return { capacity, minimized: enforceCapacity(s.openChats, s.minimized, capacity) };
    }),

  openChat: (id) =>
    set((s) => {
      const conversations = new Map(s.conversations);
      const meta = conversations.get(id);
      if (meta && meta.unreadCount !== 0) {
        conversations.set(id, { ...meta, unreadCount: 0, unreadUpdatedAt: undefined });
      }
      const openChats = s.openChats.includes(id) ? s.openChats : [...s.openChats, id];
      const restored = new Set(s.minimized);
      restored.delete(id);
      const minimized = enforceCapacity(openChats, restored, s.capacity, id);
      return { conversations, openChats, minimized };
    }),

  closeChat: (id) =>
    set((s) => {
      if (!s.openChats.includes(id)) return s;
      const minimized = new Set(s.minimized);
      minimized.delete(id);
      return { openChats: s.openChats.filter((c) => c !== id), minimized };
    }),

  minimizeChat: (id) =>
    set((s) => {
      if (!s.openChats.includes(id) || s.minimized.has(id)) return s;
      const minimized = new Set(s.minimized);
      minimized.add(id);
      return { minimized };
    }),

  setMyId: (id) => set({ myId: id }),

  // Merge the authoritative server list into the store instead of
  // replacing it. A stale MyConversations response must not drop a
  // conversation a lifecycle CREATED event added in the meantime.
  // Removals are event/action-driven (lifecycle DELETED, removeConversation),
  // never inferred from this list's absence.
  setConversations: (convos) =>
    set((s) => {
      const next = new Map(s.conversations);
      for (const c of convos) {
        const ex = next.get(c.id);
        next.set(c.id, {
          ...ex,
          id: c.id,
          participantIds: c.participantIds,
          unreadCount: ex?.unreadCount ?? c.unreadCount,
        });
      }
      return { conversations: next };
    }),

  addConversation: (convo) =>
    set((s) => {
      const next = new Map(s.conversations);
      next.set(convo.id, convo);
      return { conversations: next };
    }),

  removeConversation: (id) =>
    set((s) => {
      if (!s.conversations.has(id)) return s;
      const conversations = new Map(s.conversations);
      conversations.delete(id);
      const messages = new Map(s.messages);
      messages.delete(id);
      const typing = new Map(s.typing);
      typing.delete(id);
      const presence = new Map(s.presence);
      presence.delete(id);
      const readReceipts = new Map(s.readReceipts);
      readReceipts.delete(id);
      const minimized = new Set(s.minimized);
      minimized.delete(id);
      const liveErrors = new Set(s.liveErrors);
      liveErrors.delete(id);
      return {
        conversations,
        messages,
        typing,
        presence,
        readReceipts,
        minimized,
        liveErrors,
        openChats: s.openChats.filter((c) => c !== id),
      };
    }),

  setMessages: (conversationId, msgs) =>
    set((s) => {
      const next = new Map(s.messages);
      next.set(conversationId, mergeMessages(next.get(conversationId) ?? [], msgs));
      return { messages: next };
    }),

  addMessage: (msg) =>
    set((s) => {
      const next = new Map(s.messages);
      const existing = next.get(msg.conversationId) ?? [];
      if (existing.some((m) => m.id === msg.id)) return s;
      next.set(msg.conversationId, [...existing, msg]);
      const convos = new Map(s.conversations);
      const meta = convos.get(msg.conversationId);
      if (meta) {
        const isMine = s.myId !== null && msg.senderId === s.myId;
        // "Seen" = a window is open AND not minimized. Mirrors the old
        // active-conversation rule but for the multi-window dock.
        const isVisible =
          s.openChats.includes(msg.conversationId) && !s.minimized.has(msg.conversationId);
        const bumpUnread = !isMine && !isVisible;
        convos.set(msg.conversationId, {
          ...meta,
          lastMessage: msg,
          unreadCount: bumpUnread ? meta.unreadCount + 1 : meta.unreadCount,
          unreadUpdatedAt: bumpUnread ? msg.sentAt : meta.unreadUpdatedAt,
        });
      }
      return { messages: next, conversations: convos };
    }),

  // Seeds the list preview from a one-time fetch. Guarded to keep the newest
  // message, so a live MESSAGE_CREATED event (newer) is never overwritten by a
  // late-arriving seed query.
  setLastMessage: (conversationId, msg) =>
    set((s) => {
      const meta = s.conversations.get(conversationId);
      if (!meta) return s;
      if (meta.lastMessage && meta.lastMessage.sentAt >= msg.sentAt) return s;
      const next = new Map(s.conversations);
      next.set(conversationId, { ...meta, lastMessage: msg });
      return { conversations: next };
    }),

  setUnreadCount: (conversationId, count) =>
    set((s) => {
      const meta = s.conversations.get(conversationId);
      if (!meta || meta.unreadCount === count) return s;
      if (count < meta.unreadCount && meta.unreadUpdatedAt) return s;
      const next = new Map(s.conversations);
      next.set(conversationId, {
        ...meta,
        unreadCount: count,
        unreadUpdatedAt:
          count === 0
            ? undefined
            : count > meta.unreadCount
              ? new Date().toISOString()
              : meta.unreadUpdatedAt,
      });
      return { conversations: next };
    }),

  setTyping: (conversationId, personId, isTyping) =>
    set((s) => {
      const next = new Map(s.typing);
      const set_ = new Set(next.get(conversationId) ?? []);
      if (isTyping) set_.add(personId);
      else set_.delete(personId);
      next.set(conversationId, set_);
      return { typing: next };
    }),

  setPresence: (conversationId, personId, online, observedAt) =>
    set((s) => {
      const conv = s.presence.get(conversationId);
      const cur = conv?.get(personId);
      // Reject stale or duplicate updates so a late seed can't undo a
      // newer event (and vice versa).
      if (cur && !isNewer(observedAt, cur.observedAt)) return s;
      const next = new Map(s.presence);
      const inner = new Map(conv ?? []);
      inner.set(personId, { online, observedAt });
      next.set(conversationId, inner);
      return { presence: next };
    }),

  setReadReceipt: (conversationId, personId, lastReadMessageId, readAt) =>
    set((s) => {
      const conv = s.readReceipts.get(conversationId);
      const cur = conv?.get(personId);
      if (cur && !isNewer(readAt, cur.readAt)) return s;
      const next = new Map(s.readReceipts);
      const inner = new Map(conv ?? []);
      inner.set(personId, { lastReadMessageId, readAt });
      next.set(conversationId, inner);
      return { readReceipts: next };
    }),

  setLiveError: (conversationId, hasError) =>
    set((s) => {
      if (hasError === s.liveErrors.has(conversationId)) return s;
      const next = new Set(s.liveErrors);
      if (hasError) next.add(conversationId);
      else next.delete(conversationId);
      return { liveErrors: next };
    }),
}));
